import { underscore } from 'inflected';
import { buildCustomField, buildCustomFieldSet } from '../models/custom_fields';
import * as FieldDefinitions from './field_definitions';
import CardStylings from './card_stylings';
import { type RecordLayout } from 'components/framework/models';
import type FieldSet from './FieldSet';
import { type ApplicationModelConstructor } from 'components/framework/ApplicationModel';
import type Field from './Field';
import { type CardLayoutModelConstructorType } from './types';
import type CardStyling from './CardStyling';
import { type CardStylingTypesEnum_value } from 'components/framework/models/generated/types';
import * as CustomFieldSets from './custom_fields/customFieldSets';

class RecordFields {
  static modelClass: ApplicationModelConstructor<any>;

  fieldSets = new Map<string, FieldSet>();
  customFieldSets = new Map<string, FieldSet>();
  fields: Field[];
  fieldHash: Record<string, Field>;

  constructor(public recordLayout: RecordLayout) {
    this.recordLayout = recordLayout;
    this.addFieldSets(FieldDefinitions);
    this.addCustomFieldSets(CustomFieldSets);

    this.buildFieldHash();
  }

  get modelClass() {
    return (this.constructor as typeof RecordFields)
      .modelClass as CardLayoutModelConstructorType;
  }

  get terminologyName() {
    return this.modelClass.terminologyName || this.modelClass.typename;
  }

  buildCustomField(
    customFieldDefinition,
    customFieldSetField: string | null = null
  ) {
    return buildCustomField(customFieldDefinition, false, customFieldSetField);
  }

  buildCustomFieldSet(customFieldDefinition) {
    return buildCustomFieldSet(customFieldDefinition);
  }

  buildCardStyling(type: CardStylingTypesEnum_value) {
    const CardStylingClass = CardStylings[type];
    if (!CardStylingClass) {
      throw new Error(`Invalid card style ${type}`);
    }

    return new CardStylingClass(this.modelClass);
  }

  get availableCardStylings(): Array<typeof CardStyling> {
    return Object.values(CardStylings).filter(CardStylingClass =>
      CardStylingClass.availableForModel(this.modelClass)
    );
  }

  keyForField(name) {
    return `${underscore(this.modelClass.typename)}.${name}`;
  }

  addFieldSets(sets) {
    Object.entries(sets).forEach(([name, FieldSet]) =>
      this.addFieldSet(name, FieldSet)
    );
  }

  addFieldSet(name, FieldSet) {
    const fieldSet = new FieldSet(this);

    // If none of the fields in the field set are valid, then we skip it
    if (fieldSet.availableFields.length) {
      this.fieldSets.set(name, fieldSet);
    }
  }

  addCustomFieldSets(sets) {
    Object.entries(sets).forEach(([name, FieldSet]) =>
      this.addCustomFieldSet(name, FieldSet)
    );
  }

  addCustomFieldSet(name, FieldSet) {
    const fieldSet = new FieldSet(this);

    // If none of the fields in the field set are valid, then we skip it
    if (fieldSet.availableFields.length) {
      this.customFieldSets.set(name, fieldSet);
    }
  }

  buildFieldHash() {
    this.fieldHash = {};
    this.fields = Array.from(this.fieldSets.values()).flatMap(
      fieldSet => fieldSet.availableFields
    );
    this.fields.forEach(
      field => (this.fieldHash[field.constructor.name] = field)
    );
  }

  field(name) {
    return this.fieldHash[name];
  }
}

export default RecordFields;
